// src/Hooks/useMapData.js

import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../Firebase/firebase';
import { useIcons } from '../Contexts/IconContext';

export const useMapData = () => {
  const [rawGroupsData, setRawGroupsData] = useState({});
  const [rawPolygonsData, setRawPolygonsData] = useState([]);
  const [rawPolylinesData, setRawPolylinesData] = useState([]);
  const [rawMarkersData, setRawMarkersData] = useState([]);

  const [polygonsData, setPolygonsData] = useState([]);
  const [polylinesData, setPolylinesData] = useState([]);
  const [mergedMarkersData, setMergedMarkersData] = useState([]);
  const [markersData, setMarkersData] = useState([]);

  const [isPermitted, setIsPermitted] = useState(false);

  const { markerIcons } = useIcons();

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user && user.email) {
        const emailKey = user.email.toLowerCase().trim();
        const unsubPerm = onSnapshot(
          doc(db, 'permittedUsers', emailKey),
          (docSnap) => {
            setIsPermitted(docSnap.exists());
          },
          (error) => {
            // Silently catch the race-condition error during sign-out
            if (error.code !== 'permission-denied') {
              console.log('Permission listener stopped:', error.code);
            }
          },
        );

        user._permUnsub = unsubPerm;
      } else {
        setIsPermitted(false);
      }
    });

    return () => {
      unsubAuth();
      if (auth.currentUser && auth.currentUser._permUnsub) {
        auth.currentUser._permUnsub();
      }
    };
  }, []);

  // AMENDED: Removed the `if (!currentUser) return;` block so the map always fetches data
  useEffect(() => {
    const unsubGroups = onSnapshot(
      query(collection(db, 'markerGroups')),
      (snapshot) => {
        const groupsMap = {};
        snapshot.docs.forEach((doc) => {
          groupsMap[doc.id] = { ...doc.data() };
        });
        setRawGroupsData(groupsMap);
      },
      (error) => console.log('Groups snapshot silent fail:', error.code),
    );

    const unsubMarkers = onSnapshot(
      query(collection(db, 'markers')),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          type: 'marker',
        }));
        setRawMarkersData(data);
      },
      (error) => console.log('Markers snapshot silent fail:', error.code),
    );

    const unsubPolys = onSnapshot(
      query(collection(db, 'polygons')),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          type: 'polygon',
        }));
        setRawPolygonsData(data);
      },
      (error) => console.log('Polygons snapshot silent fail:', error.code),
    );

    const unsubLines = onSnapshot(
      query(collection(db, 'polylines')),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          type: 'line',
        }));
        setRawPolylinesData(data);
      },
      (error) => console.log('Lines snapshot silent fail:', error.code),
    );

    return () => {
      unsubGroups();
      unsubMarkers();
      unsubPolys();
      unsubLines();
    };
  }, []); // Run once on mount, regardless of auth state

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    const isVisible = (mergedObj) => {
      if (isPermitted) return true;
      const visibility =
        mergedObj.visibility || (mergedObj.isPrivate ? 'PRIVATE' : 'PUBLIC');
      if (visibility === 'PRIVATE') return false;
      if (visibility === 'PUBLIC_FROM') {
        if (!mergedObj.publicFromDate || mergedObj.publicFromDate > today) return false;
      }
      return true;
    };

    const groupByMarkerId = {};
    const groupByPolygonId = {};
    const groupByPolylineId = {};

    Object.keys(rawGroupsData).forEach((groupId) => {
      const group = rawGroupsData[groupId];
      groupByMarkerId[groupId] = group;
      groupByPolygonId[groupId] = group;
      groupByPolylineId[groupId] = group;

      if (group.markerID) groupByMarkerId[group.markerID] = group;
      if (group.polygonID) groupByPolygonId[group.polygonID] = group;
      if (group.polylineID) groupByPolylineId[group.polylineID] = group;
    });

    // AMENDED: Strip rogue media fields from geometry objects before merging so they never overwrite rich data
    const finalPolygons = rawPolygonsData.reduce((acc, geom) => {
      const groupData =
        groupByPolygonId[geom.markerGroupID] || groupByPolygonId[geom.id] || {};
      const cleanGeom = { ...geom };
      delete cleanGeom.photos;
      delete cleanGeom.documents;

      const merged = { ...groupData, ...cleanGeom };
      if (isVisible(merged)) acc.push(merged);
      return acc;
    }, []);

    const finalPolylines = rawPolylinesData.reduce((acc, geom) => {
      const groupData =
        groupByPolylineId[geom.markerGroupID] || groupByPolylineId[geom.id] || {};
      const cleanGeom = { ...geom };
      delete cleanGeom.photos;
      delete cleanGeom.documents;

      const merged = { ...groupData, ...cleanGeom };
      if (isVisible(merged)) acc.push(merged);
      return acc;
    }, []);

    const finalMarkers = rawMarkersData.reduce((acc, geom) => {
      const groupData =
        groupByMarkerId[geom.markerGroupID] || groupByMarkerId[geom.id] || {};
      const cleanGeom = { ...geom };
      delete cleanGeom.photos;
      delete cleanGeom.documents;

      const merged = { ...groupData, ...cleanGeom };
      if (isVisible(merged)) acc.push(merged);
      return acc;
    }, []);

    // 2. LEGACY FAILSAFE: Rescue any unsplit shapes stuck entirely inside markerGroups
    Object.keys(rawGroupsData).forEach((groupId) => {
      const group = rawGroupsData[groupId];

      if (
        group.position &&
        !finalMarkers.some((m) => m.id === group.markerID || m.id === groupId)
      ) {
        const rescuedMarker = { ...group, id: group.markerID || groupId, type: 'marker' };
        if (isVisible(rescuedMarker)) finalMarkers.push(rescuedMarker);
      }
      if (
        (group.paths || group.path) &&
        group.polygonID &&
        !finalPolygons.some((p) => p.id === group.polygonID)
      ) {
        const rescuedPoly = {
          ...group,
          id: group.polygonID,
          paths: group.paths || group.path,
          type: 'polygon',
        };
        if (isVisible(rescuedPoly)) finalPolygons.push(rescuedPoly);
      }
      if (
        (group.path || group.paths) &&
        group.polylineID &&
        !finalPolylines.some((l) => l.id === group.polylineID)
      ) {
        const rescuedLine = {
          ...group,
          id: group.polylineID,
          path: group.path || group.paths,
          type: 'line',
        };
        if (isVisible(rescuedLine)) finalPolylines.push(rescuedLine);
      }
    });

    setPolygonsData(finalPolygons);
    setPolylinesData(finalPolylines);
    setMergedMarkersData(finalMarkers);
  }, [rawGroupsData, rawPolygonsData, rawPolylinesData, rawMarkersData, isPermitted]);

  useEffect(() => {
    if (!markerIcons || markerIcons.length === 0) {
      setMarkersData(mergedMarkersData);
      return;
    }

    const iconLookup = {};
    markerIcons.forEach((icon) => {
      iconLookup[icon.id] = icon.icon;
    });

    const enriched = mergedMarkersData.map((marker) => {
      let resolvedIcon = marker.icon;
      if (marker.iconID && iconLookup[marker.iconID]) {
        resolvedIcon = iconLookup[marker.iconID];
      }
      return { ...marker, icon: resolvedIcon };
    });

    setMarkersData(enriched);
  }, [mergedMarkersData, markerIcons]);

  return {
    markersData,
    setMarkersData,
    polygonsData,
    setPolygonsData,
    polylinesData,
    setPolylinesData,
  };
};
